import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';

const FeaturesSection = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const features = [
    {
      icon: 'Users',
      title: 'Управление лидами',
      description: 'Автоматический захват, квалификация и распределение лидов с AI-скорингом',
      benefits: ['Автозахват из всех каналов', 'AI-скоринг качества', 'Умное распределение'],
      color: 'primary'
    },
    {
      icon: 'TrendingUp',
      title: 'Воронка продаж',
      description: 'Визуализация и автоматизация всех этапов сделки с прогнозной аналитикой',
      benefits: ['Настраиваемые этапы', 'Автоматические действия', 'Прогноз закрытия'],
      color: 'accent'
    },
    {
      icon: 'Bot',
      title: 'ИИ-ассистент Виктория',
      description: 'Умный помощник для автоматизации рутинных задач и анализа данных',
      benefits: ['Обработка запросов', 'Генерация отчетов', 'Рекомендации по сделкам'],
      color: 'secondary'
    },
    {
      icon: 'Blocks',
      title: 'Кастомные модули',
      description: 'Создавайте собственные модули под специфику вашего бизнеса',
      benefits: ['Конструктор модулей', 'Гибкие поля', 'Интеграции API'],
      color: 'success'
    }
  ];

  return (
    <section id="features" ref={sectionRef} className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card to-background" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground mb-4">
            Все инструменты в одной платформе
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            СФЕРА объединяет все необходимые модули для управления бизнесом
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            {features?.map((feature, index) => (
              <div
                key={index}
                onClick={() => setActiveFeature(index)}
                className={`glassmorphism rounded-xl p-6 cursor-pointer transition-all duration-500 ${
                  activeFeature === index ? 'border-2 border-primary neon-glow-primary scale-105' : 'hover:scale-102'
                } ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg bg-${feature?.color}/10 flex items-center justify-center flex-shrink-0`}>
                    <Icon name={feature?.icon} size={24} color={`var(--color-${feature?.color})`} />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      {feature?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {feature?.description}
                    </p>
                    
                    {activeFeature === index && (
                      <div className="space-y-2 animate-fade-in">
                        {feature?.benefits?.map((benefit, i) => (
                          <div key={i} className="flex items-center space-x-2">
                            <Icon name="Check" size={16} color="var(--color-success)" />
                            <span className="text-sm text-muted-foreground">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <Icon 
                    name={activeFeature === index ? 'ChevronDown' : 'ChevronRight'} 
                    size={20} 
                    color="var(--color-muted-foreground)" 
                  />
                </div>
              </div>
            ))}
          </div>

          <div className={`glassmorphism-strong rounded-2xl p-8 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <div className="aspect-square bg-gradient-to-br from-card via-background to-card rounded-xl p-6 flex items-center justify-center">
              <div className="text-center space-y-6">
                <div className={`w-24 h-24 mx-auto rounded-2xl bg-${features?.[activeFeature]?.color}/10 flex items-center justify-center neon-glow-${features?.[activeFeature]?.color}`}>
                  <Icon 
                    name={features?.[activeFeature]?.icon} 
                    size={48} 
                    color={`var(--color-${features?.[activeFeature]?.color})`} 
                  />
                </div>
                
                <div>
                  <h4 className="text-2xl font-bold text-foreground mb-2">
                    {features?.[activeFeature]?.title}
                  </h4>
                  <p className="text-muted-foreground">
                    {features?.[activeFeature]?.description}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-6">
                  <div className="glassmorphism rounded-lg p-4">
                    <div className="text-2xl font-bold text-primary mb-1">99%</div>
                    <div className="text-xs text-muted-foreground">Точность</div>
                  </div>
                  <div className="glassmorphism rounded-lg p-4">
                    <div className="text-2xl font-bold text-accent mb-1">5x</div>
                    <div className="text-xs text-muted-foreground">Быстрее</div>
                  </div>
                  <div className="glassmorphism rounded-lg p-4">
                    <div className="text-2xl font-bold text-secondary mb-1">24/7</div>
                    <div className="text-xs text-muted-foreground">Работа</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;